import { Injectable } from "@angular/core";
import { Subject } from 'rxjs';
import {
  Acabado,
  ColorMaterial,
  Continente,
  TipoMaterial,
  UsoMaterial,
  FilterType,
  Maestros,
  Espesor,
  FilterLogicaOperator,
  FilterComparisonOperator,
  Filter,
  FilterKey,
  FilterGroupLogicaOperator,
  Disponibilidad,
  RangoPrecio,
  FilterParam,
  getfilterLogicalOperatorParam,
  getfilterLogicalOperator,
  Material
} from "../models";

@Injectable({ providedIn: 'root' })
export class FiltersService {

  onScrolled$: Subject<void> = new Subject<void>()

  constructor() { }

  getFiltersFromMaestros(maestros: Maestros): Filter[] {
    return [
      ...maestros.tipoMaterial.map(tipo => this.getTipoMaterialFilterNode(tipo)),
      ...maestros.acabado.map(acabado => this.getAcabadoFilterNode(acabado)),
      ...maestros.colorMaterial.map(color => this.getColorFilterNode(color)),
      ...maestros.usoMaterial.map(uso => this.getUsoMaterialFilterNode(uso)),
      ...maestros.continente.map(origen => this.getOrigenFilterNode(origen)),
      ...maestros.disponibilidad.map(disponibilidad => this.getDisponibilidadFilterNode(disponibilidad)),
      ...maestros.rangoPrecio.map(rango => this.getRangoPrecioFilterNode(rango)),
      ...maestros.materiales.map(material => this.getMaterialFilterNode(material))
    ]
  }

  getMaterialFilterNode(material: Material): Filter {
    return {
      type: FilterType.CHECK_BOX,
      key: FilterKey.MATERIAL,
      name: material.NOMBRE,
      value: material.CODIGO,
      groupLogicalOperator: FilterGroupLogicaOperator.OR,
      logicalOperator: FilterLogicaOperator.AND,
      comparator: FilterComparisonOperator.EQUAL
    }
  }

  getTipoMaterialFilterNode(tipoMaterial: TipoMaterial): Filter {
    return {
      name: tipoMaterial.DESCRIPCION,
      value: tipoMaterial.TIPOMATERIALID,
      key: FilterKey.TIPOLOGIA,
      type: FilterType.CHECK_BOX,
      logicalOperator: FilterLogicaOperator.AND,
      comparator: FilterComparisonOperator.EQUAL,
      groupLogicalOperator: FilterGroupLogicaOperator.OR
    }
  }

  getAcabadoFilterNode(acabado: Acabado): Filter {
    return {
      name: acabado.NOMBRE,
      value: acabado.CODIGO,
      key: FilterKey.ACABADO,
      type: FilterType.CHECK_BOX,
      logicalOperator: FilterLogicaOperator.AND,
      comparator: FilterComparisonOperator.EQUAL,
      groupLogicalOperator: FilterGroupLogicaOperator.OR
    }
  }

  getUsoMaterialFilterNode(usoMaterial: UsoMaterial): Filter {
    return {
      name: usoMaterial.DESCRIPCION,
      value: usoMaterial.USOMATERIALID,
      key: FilterKey.USOS,
      type: FilterType.CHECK_BOX,
      logicalOperator: FilterLogicaOperator.AND,
      comparator: FilterComparisonOperator.EQUAL,
      groupLogicalOperator: FilterGroupLogicaOperator.OR
    }
  }

  getColorFilterNode(colorMaterial: ColorMaterial): Filter {
    return {
      name: colorMaterial.DESCRIPCION,
      value: colorMaterial.COLORMATERIALID,
      type: FilterType.CHECK_BOX,
      key: FilterKey.COLOR,
      color: colorMaterial.COLOR,
      logicalOperator: FilterLogicaOperator.AND,
      comparator: FilterComparisonOperator.EQUAL,
      groupLogicalOperator: FilterGroupLogicaOperator.OR
    }
  }

  getOrigenFilterNode(continente: Continente): Filter {
    return {
      name: continente.DESCRIPCION,
      value: continente.CONTINENTEID,
      key: FilterKey.ORIGEN,
      type: FilterType.CHECK_BOX,
      logicalOperator: FilterLogicaOperator.AND,
      comparator: FilterComparisonOperator.EQUAL,
      groupLogicalOperator: FilterGroupLogicaOperator.OR
    }
  }

  getEspesorFilterNode(espesor: Espesor): Filter {
    return {
      name: espesor.DESCRIPCION,
      value: espesor.CONDICION,
      type: FilterType.CHECK_BOX,
      key: FilterKey.ESPESOR,
      logicalOperator: FilterLogicaOperator.AND,
      comparator: FilterComparisonOperator.CUSTOM,
      groupLogicalOperator: FilterGroupLogicaOperator.OR
    }
  }

  getDisponibilidadFilterNode(disponibilidad: Disponibilidad): Filter {
    return {
      name: disponibilidad.DESCRIPCION,
      value: disponibilidad.CODIGO,
      type: FilterType.CHECK_BOX,
      key: FilterKey.DISPONIBILIDAD,
      logicalOperator: FilterLogicaOperator.AND,
      comparator: FilterComparisonOperator.EQUAL,
      groupLogicalOperator: FilterGroupLogicaOperator.OR
    }
  }

  getRangoPrecioFilterNode(rangoPrecio: RangoPrecio): Filter {
    return {
      name: rangoPrecio.DESCRIPCION,
      value: rangoPrecio.CODIGO,
      type: FilterType.INLINE_CHECK_BOX,
      key: FilterKey.RANGO_PRECIO,
      logicalOperator: FilterLogicaOperator.AND,
      comparator: FilterComparisonOperator.EQUAL,
      groupLogicalOperator: FilterGroupLogicaOperator.OR
    }
  }

  deserializeFiltersParams(paramsStr: string): Filter[] {
    try {
      const params: string[] = atob(paramsStr).split('&');
      return params.map(p => this.deserializeFilterParam(<FilterParam>JSON.parse(p)))
    } catch (err) {
      return [];
    }
  }

  deserializeFilterParam(param: FilterParam): Filter {
    return {
      logicalOperator: getfilterLogicalOperator(param[0]),
      groupLogicalOperator : param[1],
      name: param[2],
      key: param[3],
      comparator: param[4],
      value: param[5],
      type: param[6]
    }
  }

  serializeFiltersParams(filters: Filter[]): string {
    return btoa(filters.map(
      filter => JSON.stringify(
        this.serializeFilterParam(filter)
        )).join('&'));
  }

  serializeFilterParam(filter: Filter): FilterParam {
    return [
      getfilterLogicalOperatorParam(filter.logicalOperator),
      filter.groupLogicalOperator,
      filter.name,
      filter.key,
      filter.comparator,
      filter.value,
      filter.type
    ]
  }

  serializeFiltersApiParams(filters: Filter[]): string[] {
    let params: string[] = [];
    const keys: Filter['key'][] = filters.map(f => f.key);
    let keysUnique: Filter['key'][] = [];
    keys.map(k => !keysUnique.find(unk => unk === k) && keysUnique.push(k));

    for (const key of keysUnique) {
      const keyfilters: Filter[] = filters.filter(f => f.key === key);
      const filter: Filter = keyfilters[0];
      const groupLogicalOperator: FilterGroupLogicaOperator = filter.groupLogicalOperator;
      const values: string = filter.type !== FilterType.CUSTOM_VALUE
      ? keyfilters.map(f => f.key + f.comparator + f.value).join(groupLogicalOperator)
      : filter.value + ''
      const logicalOperator: FilterLogicaOperator = filter.logicalOperator;
      params.push(logicalOperator + values);
    }

    return params;
  }

}
